import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Autocomplete,
  Tooltip,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../../contexts/AuthContext';
import { CategoryService } from './services/categoryService';
import type { InventoryCategoryDto, CreateInventoryCategoryDto } from './services/categoryService';
import { useTranslation } from 'react-i18next';

// Kategori veri yapısı
interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
  isEcommerceEnabled?: boolean;
  orderIndex?: number;
  isActive?: boolean;
  description?: string;
  imageUrl?: string;
  headerImageUrl?: string;
}

interface Branch {
  id: string;
  name: string;
}

interface InventoryCategoryModalProps {
  open: boolean;
  onClose: () => void;
  branchId?: string;
  isManager?: boolean;
}

const InventoryCategoryModal: React.FC<InventoryCategoryModalProps> = ({ 
  open, 
  onClose, 
  branchId, 
  isManager = false 
}) => {
  const { t, i18n } = useTranslation('accounting');
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches, userDetails } = useAuth();
  
  // 🔍 OFFLINE DEBUG
  const hasLoggedRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !hasLoggedRef.current) {
      hasLoggedRef.current = true;
      console.log('🚀 [CATEGORY-MODAL] Modal açıldı');
      console.log('🔍 [CATEGORY-MODAL] AuthContext branches:', branches?.length || 0, 'adet');
      console.log('🔍 [CATEGORY-MODAL] AuthContext userDetails:', userDetails ? `✅ companyId: ${userDetails.companyId}` : '❌ YOK');
    }
    if (!open) {
      hasLoggedRef.current = false; // Modal kapandığında reset
    }
  }, [open, branches, userDetails]);
  
  const [categoryName, setCategoryName] = useState('');
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<InventoryCategory | null>(null);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<InventoryCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [showEcommerceInfo, setShowEcommerceInfo] = useState<boolean>(false);
  
  // Kategori düzenleme için yeni state'ler
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editCategoryName, setEditCategoryName] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  
  // Kategori silme onay modalı için state'ler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [categoryToDelete, setCategoryToDelete] = useState<InventoryCategory | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  
  // Uyarı modalı için state'ler
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [warningTitle, setWarningTitle] = useState<string>(t('stock.modals.category.warning'));
  
  // Şirket ID'sini, şubeleri ve kategorileri yükleme
  useEffect(() => {
    const loadCompanyAndData = async () => {
      console.log('🔄 [CATEGORY-MODAL] loadCompanyAndData başladı...');
      
      try {
        setInitialLoading(true);
        
        const cId = userDetails?.companyId;
        if (!cId) {
          console.error('❌ [CATEGORY-MODAL] AuthContext\'te companyId yok');
          setInitialLoading(false);
          return;
        }
        
        console.log('✅ [CATEGORY-MODAL] AuthContext\'ten companyId alındı:', cId);
        setCompanyId(cId);
        
        // Kategorileri yükle
        console.log('📥 [CATEGORY-MODAL] Kategoriler yükleniyor (SQL API)...');
        await loadInventoryCategories(cId);
        
      } catch (error) {
        console.error('❌ [CATEGORY-MODAL] Veri yüklenirken hata:', error);
        setInitialLoading(false);
      }
    };
    
    if (open) {
      loadCompanyAndData();
    }
    
    // Cleanup
  }, [open, branchId, userDetails?.companyId]);
  
  // ✅ Şube müdürü için otomatik şube seçimi (branches değiştiğinde)
  useEffect(() => {
    if (isManager && branchId && branches.length > 0) {
      const managerBranch = branches.find(branch => branch.id === branchId);
      if (managerBranch) {
        setSelectedBranch(managerBranch);
        console.log(`Şube müdürü için otomatik şube seçildi: ${managerBranch.name}`);
      }
    }
  }, [branches, isManager, branchId]);
  
  // Ürün kategorilerini yükleme - SQL'den
  const loadInventoryCategories = async (companyId: string) => {
    try {
      console.log('🌐 [CATEGORY-MODAL] SQL API çağrısı başlıyor...');
      
      const categoriesData = await CategoryService.getAllCategories(companyId);
      
      console.log('✅ [CATEGORY-MODAL] SQL\'den', categoriesData.length, 'kategori yüklendi');
      
      // DTO'dan local interface'e dönüştür
      const categoriesList: InventoryCategory[] = categoriesData.map((cat: InventoryCategoryDto) => ({
        id: cat.id,
        name: cat.name,
        parentId: cat.parentId || null,
        level: cat.level || 1, // Level 0 ise 1 yap (eski veri için koruma)
        createdAt: cat.createdAt,
        branchesId: null, // SQL'de branch bilgisi yok, şimdilik null
        isEcommerceEnabled: cat.isEcommerceEnabled || false,
        orderIndex: cat.orderIndex || 0,
        isActive: cat.isActive !== false, // undefined ise true kabul et
        description: cat.description || undefined,
        imageUrl: cat.imageUrl || undefined,
        headerImageUrl: cat.headerImageUrl || undefined,
      }));
      
      console.log('🎉 [CATEGORY-MODAL] Sayfa yükleme TAMAMLANDI');
      
      // Verileri seviyeye göre sıralayalım
      categoriesList.sort((a, b) => {
        // Önce seviyeye göre sırala
        if (a.level !== b.level) {
          return a.level - b.level;
        }
        // Sonra isme göre sırala
        return a.name.localeCompare(b.name);
      });
      
      setCategories(categoriesList);
      
      // Kategorilere filtreleme uygula
      applyBranchFilter(categoriesList, selectedBranch?.id);
      
    } catch (error: any) {
      console.error('❌ [CATEGORY-MODAL] SQL yükleme hatası:', error.message || error);
      console.error('❌ [CATEGORY-MODAL] Detay:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'Kategoriler yüklenirken hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setInitialLoading(false);
    }
  };
  
  // Şubeye göre kategorileri filtrele
  const applyBranchFilter = (allCategories: InventoryCategory[], branchId: string | null | undefined) => {
    if (!branchId) {
      // Şube seçilmemişse veya managerBranchId yoksa tüm kategorileri göster
      setFilteredCategories(allCategories);
      return;
    }
    
    console.log(`Kategoriler ${branchId} şube ID'sine göre filtreleniyor...`);
    
    // Şubeye ait kategorileri filtrele
    const filtered = allCategories.filter(category => {
      // Eğer parentId'si olan bir kategori ise, üst kategorinin filtrelemesine bağlı
      if (category.parentId) {
        const parentCategory = allCategories.find(p => p.id === category.parentId);
        return parentCategory ? isBranchCategory(parentCategory, branchId) : false;
      }
      
      // Ana kategori için doğrudan şube kontrolü yap
      return isBranchCategory(category, branchId);
    });
    
    console.log(`Filtreleme sonucu: ${filtered.length}/${allCategories.length} kategori`);
    setFilteredCategories(filtered);
  };
  
  // Kategorinin verilen şubeye ait olup olmadığını kontrol et
  const isBranchCategory = (category: InventoryCategory, branchId: string): boolean => {
    if (!category.branchesId) {
      // branchesId yoksa genel kategori olabilir, şube müdürü yetkisine göre karar ver
      return !isManager; // Şube müdürü değilse göster, şube müdürü ise gizle
    }
    
    // branchesId obje formatında olabilir
    if (typeof category.branchesId === 'object' && category.branchesId !== null) {
      const branchKeys = Object.keys(category.branchesId);
      return branchKeys.includes(branchId);
    }
    
    // String formatında karşılaştırma
    return category.branchesId === branchId;
  };
  
  // Şube değiştiğinde kategorileri filtrele
  useEffect(() => {
    if (categories.length > 0) {
      applyBranchFilter(categories, selectedBranch?.id);
    }
  }, [selectedBranch, categories]);
  
  // Formun temizlenmesi
  const resetForm = () => {
    setCategoryName('');
    setError('');
    setIsEditMode(false);
    setEditCategoryName('');
  };
  
  // Modal kapatılırken çalışacak handler
  const handleClose = () => {
    resetForm();
    setSelectedCategory(null);
    onClose();
  };
  
  // Kategori seçimi
  const handleSelectCategory = (category: InventoryCategory) => {
    setSelectedCategory(category);
    
    // Eğer bir kategori seçildiyse, onun şubesini otomatik olarak seç
    if (category) {
      if (category.branchesId) {
        // Seçilen kategorinin şubesi varsa o şubeyi bul ve seç
        const categoryBranch = branches.find(b => b.id === category.branchesId);
        if (categoryBranch) {
          setSelectedBranch(categoryBranch);
        }
      } else {
        // Seçilen kategorinin şubesi yoksa (genel ise), seçimi temizle
        setSelectedBranch(null);
      }
    }
    
    // Add category alanlarını temizle (editCategoryName hariç)
    setCategoryName('');
    setError('');
    setIsEditMode(false);
    
    // Düzenleme alanını mevcut kategori adıyla doldur
    setEditCategoryName(category.name);
  };
  
  // Kategori genişletme/daraltma durumunu değiştir
  const toggleCategoryExpand = (categoryId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  // Bir kategorinin alt kategorileri var mı kontrol et
  const hasChildCategories = (categoryId: string) => {
    return filteredCategories.some(category => category.parentId === categoryId);
  };
  
  // Kategori silme onay modalını aç
  const handleDeleteCategory = (category: InventoryCategory, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Alt kategorileri kontrol et
    const hasChildren = filteredCategories.some(c => c.parentId === category.id);
    
    if (hasChildren) {
      setWarningTitle('Alt Kategoriler Mevcut');
      setWarningMessage('Bu kategorinin alt kategorileri var. Önce alt kategorileri silmelisiniz.');
      setWarningModalOpen(true);
      return;
    }
    
    // E-ticaret kontrolü
    if (category.isEcommerceEnabled) {
      setWarningTitle('E-Ticaret Uyarısı');
      setWarningMessage('Bu kategori e-ticarete açık. Silmek için önce e-ticaretten kaldırmalısınız.');
      setWarningModalOpen(true);
      return;
    }
    
    // Onay modalını aç
    setCategoryToDelete(category);
    setDeleteConfirmOpen(true);
  };
  
  // Kategori silme onay modalını kapat
  const handleCloseDeleteConfirm = () => {
    if (!isDeleting) {
      setDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    }
  };
  
  // Kategori silme işlemini onayla ve gerçekleştir - SQL
  const confirmDeleteCategory = async () => {
    if (!categoryToDelete || !companyId) {
      setDeleteConfirmOpen(false);
      return;
    }
    
    try {
      setIsDeleting(true);
      
      console.log('✅ [SQL] Kategori siliniyor:', categoryToDelete.id);
      
      // CategoryService kullanarak sil
      await CategoryService.deleteCategory(companyId, categoryToDelete.id);
      
      console.log('✅ [SQL] Kategori başarıyla silindi');
      
      // State'den kaldır
      setCategories(prevCategories => {
        const updatedCategories = prevCategories.filter(c => c.id !== categoryToDelete.id);
        applyBranchFilter(updatedCategories, selectedBranch?.id);
        return updatedCategories;
      });
      
      // Başarı mesajı
      setSnackbarMessage('Kategori başarıyla silindi');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Eğer silinen kategori seçili kategoriyse, seçimi temizle
      if (selectedCategory?.id === categoryToDelete.id) {
        setSelectedCategory(null);
      }
      
      // Modalı kapat
      setDeleteConfirmOpen(false);
      setCategoryToDelete(null);
      
    } catch (error) {
      console.error('❌ [SQL] Kategori silinirken hata:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'Kategori silinirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Yeni kategori ekleme
  const handleAddCategory = async () => {
    if (!categoryName.trim()) {
      setError('Kategori adı boş olamaz');
      return;
    }
    
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı');
      return;
    }
    
    let newCategoryLevel = 1; // Default: ana kategori (level 1)
    let parentId: string | null = null;
    let newCategoryBranchesId = null;
    
    // Eğer bir kategori seçilmişse, onun alt kategorisi olarak ekle
    if (selectedCategory) {
      // Seçilen kategori 3. seviyeyse, alt kategori eklenemez
      if (selectedCategory.level >= 3) {
        setError('Bu kategoriye alt kategori eklenemez. Maksimum 3 seviye destekleniyor.');
        return;
      }
      
      newCategoryLevel = selectedCategory.level + 1;
      parentId = selectedCategory.id;
      // Üst kategorinin şube bilgisini al
      newCategoryBranchesId = selectedCategory.branchesId;
    } else {
      // Ana kategori ekleniyor, seçilen şubeyi kullan
      newCategoryBranchesId = selectedBranch?.id || (isManager && branchId ? branchId : null);
    }
    
    try {
      setLoading(true);
      
      console.log('✅ [SQL] Yeni kategori ekleniyor...');
      console.log('✅ [SQL] Parent ID:', parentId);
      console.log('✅ [SQL] Level:', newCategoryLevel);
      
      // SQL'e yeni kategori ekle
      const newCategoryDto: CreateInventoryCategoryDto = {
        companyId: companyId,
        name: categoryName.trim(),
        parentId: parentId,
        isEcommerceEnabled: false,
        orderIndex: 0,
        isActive: true
      };
      
      const createdCategory = await CategoryService.createCategory(newCategoryDto);
      
      console.log('✅ [SQL] Kategori başarıyla eklendi:', createdCategory);
      
      // State'i güncelle
      const newCategory: InventoryCategory = {
        id: createdCategory.id,
        name: createdCategory.name,
        parentId: createdCategory.parentId || null,
        level: createdCategory.level,
        createdAt: createdCategory.createdAt,
        branchesId: newCategoryBranchesId,
        isEcommerceEnabled: createdCategory.isEcommerceEnabled || false
      };
      
      setCategories(prevCategories => {
        const updatedCategories = [...prevCategories, newCategory];
        // Seviyeye göre sırala
        updatedCategories.sort((a, b) => {
          if (a.level !== b.level) {
            return a.level - b.level;
          }
          return a.name.localeCompare(b.name);
        });
        applyBranchFilter(updatedCategories, selectedBranch?.id);
        return updatedCategories;
      });
      
      setSnackbarMessage('Kategori başarıyla eklendi');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Formu temizle
      setCategoryName('');
      setSelectedCategory(null);
      setError('');
      
    } catch (error) {
      console.error('❌ [SQL] Kategori ekleme hatası:', error);
      setError(error instanceof Error ? error.message : 'Kategori eklenirken bir hata oluştu');
      setSnackbarMessage(error instanceof Error ? error.message : 'Kategori eklenirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Ağaç yapısı için ana kategorileri bul (parentId'si null olanlar)
  const getRootCategories = () => {
    console.log('Ana kategoriler bulunuyor, mevcut kategoriler:', filteredCategories);
    const rootCategories = filteredCategories.filter(category => category.parentId === null);
    console.log('Bulunan ana kategoriler:', rootCategories);
    return rootCategories;
  };
  
  // Bir kategorinin alt kategorilerini bul
  const getChildCategories = (parentId: string) => {
    return filteredCategories.filter(category => category.parentId === parentId);
  };
  
  // Ana kategorileri ve genişletilmiş kategorilerin alt kategorilerini içeren düzleştirilmiş liste oluştur
  const getFilteredHierarchicalList = () => {
    const result: InventoryCategory[] = [];
    
    // Ana kategorileri ekle
    const rootCategories = getRootCategories();
    rootCategories.forEach(rootCategory => {
      // Ana kategoriyi ekle
      result.push(rootCategory);
      
      // Eğer kategori genişletilmişse, alt kategorileri rekürsif olarak ekle
      if (expandedCategories[rootCategory.id]) {
        addVisibleChildCategories(rootCategory.id, result, new Set([rootCategory.id]));
      }
    });
    
    return result;
  };
  
  // Görünür alt kategorileri rekürsif olarak ekle
  const addVisibleChildCategories = (parentId: string, list: InventoryCategory[], expandedParents: Set<string>) => {
    const childCategories = getChildCategories(parentId);
    
    // Alt kategorileri isme göre sırala
    childCategories.sort((a, b) => a.name.localeCompare(b.name));
    
    childCategories.forEach(childCategory => {
      // Alt kategoriyi listeye ekle
      list.push(childCategory);
      
      // Eğer bu alt kategori genişletilmişse, onun alt kategorilerini de ekle
      if (expandedCategories[childCategory.id]) {
        // Döngüsel bağımlılıkları önlemek için kontrol
        if (!expandedParents.has(childCategory.id)) {
          const newExpandedParents = new Set(expandedParents);
          newExpandedParents.add(childCategory.id);
          addVisibleChildCategories(childCategory.id, list, newExpandedParents);
        }
      }
    });
  };

  // Şube adını getir
  const getBranchName = (branchId: string | null | undefined): string => {
    if (!branchId) return t('stock.modals.category.general');
    
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : t('stock.modals.category.unknownBranch');
  };
  
  // Mevcut seçimin durumuna göre başlık
  const getFormTitle = () => {
    if (!selectedCategory) {
      return t('stock.modals.category.form.newRoot');
    }
    return t('stock.modals.category.form.newSub', { category: selectedCategory.name });
  };

  // Kategori ismini güncelleme - SQL API
  const handleUpdateCategoryName = async () => {
    if (!editCategoryName.trim()) {
      setError('Kategori adı boş olamaz');
      return;
    }
    
    if (!selectedCategory || !companyId) {
      setError('Kategori veya şirket bilgisi bulunamadı');
      return;
    }
    
    try {
      setIsUpdating(true);
      setError('');
      
      // SQL API ile kategori ismini güncelle
      await CategoryService.updateCategory({
        companyId: companyId,
        id: selectedCategory.id,
        name: editCategoryName.trim(),
        parentId: selectedCategory.parentId,
        isEcommerceEnabled: selectedCategory.isEcommerceEnabled || false,
        orderIndex: selectedCategory.orderIndex || 0,
        isActive: selectedCategory.isActive !== false,
        description: selectedCategory.description,
        imageUrl: selectedCategory.imageUrl,
        headerImageUrl: selectedCategory.headerImageUrl,
      });
      
      // State'i güncelle
      setCategories(prevCategories => {
        const updatedCategories = prevCategories.map(cat =>
          cat.id === selectedCategory.id
            ? { ...cat, name: editCategoryName.trim() }
            : cat
        );
        applyBranchFilter(updatedCategories, selectedBranch?.id);
        return updatedCategories;
      });
      
      // Seçili kategoriyi de güncelle
      setSelectedCategory(prev => prev ? { ...prev, name: editCategoryName.trim() } : null);
      
      // Başarı mesajı
      setSnackbarMessage(`Kategori ismi "${editCategoryName.trim()}" olarak güncellendi`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Düzenleme alanını temizle ama kategori seçimini koru
      setEditCategoryName('');
      
    } catch (error) {
      console.error('❌ Kategori ismi güncellenirken hata:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'Kategori ismi güncellenirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsUpdating(false);
    }
  };

  // E-ticaret durumunu değiştir - SQL
  const toggleEcommerceStatus = async (category: InventoryCategory, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!companyId) {
      setSnackbarMessage('Şirket bilgisi bulunamadı');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setLoading(true);
      
      // Kategorinin e-ticaret durumunu değiştir
      const newStatus = !category.isEcommerceEnabled;
      
      console.log('✅ [SQL] Kategori e-ticaret durumu değiştiriliyor:', { categoryId: category.id, newStatus });
      
      // CategoryService kullanarak e-ticaret durumunu değiştir
      await CategoryService.toggleEcommerceStatus(companyId, category.id, newStatus);
      
      console.log('✅ [SQL] Kategori e-ticaret durumu başarıyla değiştirildi');
      
      // State'i güncelle
      setCategories(prevCategories => {
        const updatedCategories = prevCategories.map(cat => 
          cat.id === category.id 
            ? { ...cat, isEcommerceEnabled: newStatus }
            : cat
        );
        applyBranchFilter(updatedCategories, selectedBranch?.id);
        return updatedCategories;
      });
      
      // Başarı mesajı
      setSnackbarMessage(
        newStatus 
          ? `"${category.name}" kategorisi e-ticarete açıldı` 
          : `"${category.name}" kategorisi e-ticaretten kaldırıldı`
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('❌ [SQL] E-ticaret durumu güncellenirken hata:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'E-ticaret durumu güncellenirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '80vh',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('stock.modals.category.title')}</Typography>
          <IconButton edge="end" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ overflow: { xs: 'auto', md: 'hidden' }, p: 0 }}>
        <Box sx={{ p: { xs: 2, md: 3 }, height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'hidden' } }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Form Alanı */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ 
                p: 2, 
                border: '1px solid #e0e0e0', 
                height: { xs: 'auto', md: '70vh' },
                maxHeight: { xs: 'none', md: '70vh' },
                overflowY: { xs: 'visible', md: 'auto' },
                position: { xs: 'relative', md: 'sticky' },
                top: 0,
                alignSelf: 'flex-start',
                // Kaydırma çubuğu stilleri
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#a8a8a8',
                  },
                },
              }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {getFormTitle()}
                </Typography>
                
                {selectedCategory && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {selectedCategory.level < 3 
                      ? t('stock.modals.category.form.subAddInfo', { category: selectedCategory.name })
                      : t('stock.modals.category.form.maxLevelReached', { category: selectedCategory.name })
                    }
                  </Alert>
                )}
                
                {/* Şube seçim alanı */}
                <Autocomplete
                  fullWidth
                  value={selectedBranch}
                  onChange={(event, newValue) => {
                    if (!(isManager && branchId) && !selectedCategory) {
                      setSelectedBranch(newValue);
                    }
                  }}
                  options={branches}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        selectedCategory 
                          ? t('stock.modals.category.form.branchSelected', { branch: selectedCategory.branchesId ? getBranchName(selectedCategory.branchesId) : t('stock.modals.category.general') })
                          : (isManager && branchId ? t('stock.modals.category.form.branchManager') : t('stock.modals.category.form.branchFilterOptional'))
                      }
                      size="small"
                      placeholder={selectedCategory ? t('stock.modals.category.form.branchInherit') : t('stock.modals.category.form.selectBranch')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        readOnly: isManager && branchId ? true : false
                      }}
                      disabled={!!((isManager && branchId) || selectedCategory !== null)}
                      sx={{ mb: 2 }}
                    />
                  )}
                  disableClearable={!!((isManager && branchId) || selectedCategory !== null)}
                  disabled={!!((isManager && branchId) || selectedCategory !== null)}
                />
                
                {/* Şube seçiliyse veya üst kategori seçiliyse bilgi mesajı göster */}
                {(selectedBranch || selectedCategory) && (
                  <Alert 
                    severity="info" 
                    variant="outlined" 
                    sx={{ mt: 1, mb: 2, fontSize: '0.75rem', py: 0.5 }}
                    icon={<BusinessIcon sx={{ fontSize: '1rem' }} />}
                  >
                    <Box>
                      {selectedCategory ? (
                        <>
                          <Typography variant="caption" display="block" fontWeight="bold">
                            {t('stock.modals.category.form.subCategoryBranchInfo')}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('stock.modals.category.form.branch')}: {selectedCategory.branchesId ? getBranchName(selectedCategory.branchesId) : t('stock.modals.category.general')}
                          </Typography>
                        </>
                      ) : selectedBranch ? (
                        <>
                          <Typography variant="caption" display="block" fontWeight="bold">
                            {t('stock.modals.category.form.branchSelectedText', { branch: selectedBranch.name })}
                            {isManager && branchId ? ` (${t('stock.modals.category.form.branchManagerSuffix')})` : ""}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {t('stock.modals.category.form.categoriesBoundToBranch')}
                          </Typography>
                        </>
                      ) : null}
                    </Box>
                  </Alert>
                )}
                
                <TextField
                  autoFocus={!selectedCategory}
                  margin="dense"
                  id="categoryName"
                  label={t('stock.modals.category.form.categoryName')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={categoryName}
                  onChange={(e) => {
                    setCategoryName(e.target.value);
                    setError('');
                  }}
                  error={!!error}
                  helperText={error}
                  sx={{ mb: 2 }}
                  disabled={selectedCategory?.level === 3 || loading}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  {selectedCategory && (
                    <Button 
                      variant="outlined" 
                      onClick={() => setSelectedCategory(null)}
                      disabled={loading}
                    >
                      {t('stock.modals.category.actions.clearSelection')}
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleAddCategory}
                    disabled={!categoryName.trim() || selectedCategory?.level === 3 || loading}
                    fullWidth={!selectedCategory}
                    sx={{ ml: selectedCategory ? 2 : 0 }}
                    startIcon={loading ? <CircularProgress size={24} /> : <AddIcon />}
                  >
                    {loading 
                      ? t('stock.common.saving')
                      : (selectedCategory ? t('stock.modals.category.actions.addSub') : t('stock.modals.category.actions.addCategory'))
                    }
                  </Button>
                </Box>
                
                {/* Kategori İsmi Düzenleme Bölümü - Alt kategori ekleme butonunun altında */}
                {selectedCategory && (
                  <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'rgba(25, 118, 210, 0.02)' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                      {t('stock.modals.category.form.renameTitle')}
                    </Typography>
                    <TextField
                      fullWidth
                      margin="dense"
                      label={t('stock.modals.category.form.newCategoryName')}
                      type="text"
                      variant="outlined"
                      value={editCategoryName}
                      onChange={(e) => {
                        setEditCategoryName(e.target.value);
                        setError('');
                      }}
                      helperText={t('stock.modals.category.form.currentName', { name: selectedCategory.name })}
                      sx={{ mb: 2 }}
                      disabled={isUpdating}
                    />
                    <Button 
                      variant="contained" 
                      color="secondary" 
                      onClick={handleUpdateCategoryName}
                      disabled={!editCategoryName.trim() || isUpdating || editCategoryName.trim() === selectedCategory.name}
                      startIcon={isUpdating ? <CircularProgress size={16} /> : undefined}
                      size="small"
                      fullWidth
                    >
                      {isUpdating ? t('stock.common.updating') : t('stock.modals.category.actions.rename')}
                    </Button>
                  </Box>
                )}
                
                {/* Alt boşluk - en alttaki butonun görünmesi için */}
                <Box sx={{ height: '30px' }} />
              </Paper>
            </Grid>
            
            {/* Ağaç Görünüm Alanı */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ 
                p: 2, 
                border: '1px solid #e0e0e0', 
                height: { xs: 'auto', md: '100%' },
                maxHeight: { xs: 'none', md: '70vh' },
                overflow: { xs: 'visible', md: 'auto' },
                pb: 3
              }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, gap: { xs: 1, md: 0 } }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('stock.modals.category.list.title')}
                  </Typography>
                  <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                    <Tooltip title={t('stock.modals.category.infoBox.tooltip')}>
                      <IconButton
                      size="small"
                      onClick={() => setShowEcommerceInfo(prev => !prev)}
                      sx={{
                          color: showEcommerceInfo ? 'primary.main' : 'text.secondary',
                          border: '1px solid',
                          borderColor: showEcommerceInfo ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          p: 0.5
                      }}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {showEcommerceInfo && (
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5, px: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ShoppingCartIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                          {t('stock.modals.category.infoBox.title')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {t('stock.modals.category.infoBox.description')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {t('stock.modals.category.infoBox.item1')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {t('stock.modals.category.infoBox.item2')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {t('stock.modals.category.infoBox.item3')}
                        </Typography>
                      </Box>
                    </Box>
                  </Alert>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                {filteredCategories.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {categories.length === 0 ? 
                      t('stock.modals.category.list.empty') : 
                      t('stock.modals.category.list.emptyForBranch')
                    }
                  </Typography>
                ) : (
                  <>
                    {/* Desktop: Tablo Görünümü */}
                    <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto', pb: 4 }}>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('stock.modals.category.list.total', { count: filteredCategories.length })}
                        </Typography>
                      </Box>
                      <Table size="small" sx={{ minWidth: 600 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell width="40%">{t('stock.modals.category.table.categoryName')}</TableCell>
                          <TableCell width="15%">{t('stock.modals.category.table.branch')}</TableCell>
                          <TableCell width="15%">{t('stock.modals.category.table.createdAt')}</TableCell>
                          <TableCell width="30%" align="right">{t('stock.modals.category.table.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getFilteredHierarchicalList().map((category) => (
                          <TableRow 
                            key={category.id} 
                            hover
                            sx={{ 
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                              ...(selectedCategory?.id === category.id ? 
                                { backgroundColor: 'rgba(25, 118, 210, 0.12)' } : {}),
                              ...(category.isEcommerceEnabled ? 
                                { borderLeft: '4px solid', borderLeftColor: 'success.main' } : {})
                            }}
                          >
                            <TableCell 
                              onClick={() => handleSelectCategory(category)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {/* Seviyeye göre girinti ekle */}
                                {Array(Math.max(0, category.level - 1)).fill(0).map((_, index) => (
                                  <Box key={index} sx={{ width: 20 }} />
                                ))}
                                
                                {/* Genişletme/daraltma ikonu */}
                                {hasChildCategories(category.id) ? (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => toggleCategoryExpand(category.id, e)}
                                    sx={{ p: 0.5, mr: 1 }}
                                  >
                                    {expandedCategories[category.id] ? 
                                      <KeyboardArrowDownIcon fontSize="small" /> : 
                                      <KeyboardArrowRightIcon fontSize="small" />
                                    }
                                  </IconButton>
                                ) : (
                                  <Box sx={{ width: 28, height: 28, mr: 1 }} /> // Boşluk bırak
                                )}
                                
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {category.isEcommerceEnabled ? (
                                    <Tooltip title={t('stock.modals.category.table.ecommerceOpenHint')}>
                                      <StorefrontIcon sx={{ 
                                        fontSize: '1.2rem', 
                                        mr: 1, 
                                        color: 'success.main'
                                      }} />
                                    </Tooltip>
                                  ) : (
                                    <CategoryIcon sx={{ 
                                      fontSize: '1.2rem', 
                                      mr: 1, 
                                      color: 'primary.main',
                                      opacity: 0.8
                                    }} />
                                  )}
                                  <Typography variant="body2" sx={{ 
                                    fontWeight: selectedCategory?.id === category.id ? 'bold' : 'normal' 
                                  }}>
                                    {category.name}
                                    {category.isEcommerceEnabled && (
                                      <Chip
                                        label={t('stock.modals.category.table.ecommerce')}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{ 
                                          ml: 1,
                                          height: 20,
                                          '& .MuiChip-label': {
                                            px: 1,
                                            fontSize: '0.65rem',
                                            fontWeight: 'medium'
                                          }
                                        }}
                                      />
                                    )}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell 
                              onClick={() => handleSelectCategory(category)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {getBranchName(category.branchesId)}
                              </Typography>
                            </TableCell>
                            <TableCell 
                              onClick={() => handleSelectCategory(category)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {new Date(category.createdAt).toLocaleDateString(dateLocale, {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant={category.isEcommerceEnabled ? "outlined" : "contained"}
                                  color={category.isEcommerceEnabled ? "warning" : "success"}
                                  onClick={(e) => toggleEcommerceStatus(category, e)}
                                  disabled={loading}
                                  startIcon={category.isEcommerceEnabled ? <RemoveShoppingCartIcon /> : <ShoppingCartIcon />}
                                  sx={{ 
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 'medium',
                                    minWidth: '130px'
                                  }}
                                >
                                  {category.isEcommerceEnabled ? t('stock.modals.category.actions.removeFromEcommerce') : t('stock.modals.category.actions.openEcommerce')}
                                </Button>
                                {category.level < 3 && (
                                  <Tooltip title={t('stock.modals.category.actions.addSub')}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSelectCategory(category);
                                        }}
                                        sx={{ color: 'primary.main' }}
                                        disabled={loading}
                                      >
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                                <Tooltip title={t('stock.common.delete')}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleDeleteCategory(category, e)}
                                      sx={{ color: 'error.main' }}
                                      disabled={loading}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>

                  {/* Mobile: Kart Görünümü */}
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('stock.modals.category.list.total', { count: filteredCategories.length })}
                      </Typography>
                    </Box>
                    
                    {getFilteredHierarchicalList().map((category) => (
                      <Paper
                        key={category.id}
                        elevation={2}
                        sx={{
                          p: 2,
                          mb: 2,
                          ml: (category.level - 1) * 2, // Seviyeye göre indent
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: selectedCategory?.id === category.id ? 'primary.main' : 
                                      category.isEcommerceEnabled ? 'success.main' : 'divider',
                          bgcolor: selectedCategory?.id === category.id ? 'primary.50' :
                                  category.isEcommerceEnabled ? 'success.50' : 'white',
                          '&:hover': {
                            boxShadow: 4,
                            borderColor: 'primary.main'
                          }
                        }}
                        onClick={() => handleSelectCategory(category)}
                      >
                        {/* Başlık: Kategori Adı + Expand/Collapse */}
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Box display="flex" alignItems="center" gap={1} flex={1}>
                            {category.isEcommerceEnabled ? (
                              <StorefrontIcon sx={{ fontSize: 20, color: 'success.main' }} />
                            ) : (
                              <CategoryIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                            )}
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                              {category.name}
                            </Typography>
                            {category.isEcommerceEnabled && (
                              <Chip
                                label={t('stock.modals.category.table.ecommerce')}
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.6rem' }}
                              />
                            )}
                          </Box>
                          
                          {/* Expand/Collapse butonu (varsa) */}
                          {hasChildCategories(category.id) && (
                            <IconButton
                              size="small"
                              onClick={(e) => toggleCategoryExpand(category.id, e)}
                              sx={{ p: 0.5 }}
                            >
                              {expandedCategories[category.id] ? 
                                <KeyboardArrowDownIcon fontSize="small" /> : 
                                <KeyboardArrowRightIcon fontSize="small" />
                              }
                            </IconButton>
                          )}
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {/* Bilgiler */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('stock.modals.category.table.branch')}:</Typography>
                          <Typography variant="caption" fontWeight="medium">
                            {getBranchName(category.branchesId)}
                          </Typography>
                        </Box>

                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="caption" color="text.secondary">{t('stock.modals.category.table.createdAt')}:</Typography>
                          <Typography variant="caption" fontWeight="medium">
                            {new Date(category.createdAt).toLocaleDateString(dateLocale, {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {/* İşlem Butonları */}
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Button
                            fullWidth
                            size="small"
                            variant={category.isEcommerceEnabled ? "outlined" : "contained"}
                            color={category.isEcommerceEnabled ? "warning" : "success"}
                            onClick={(e) => toggleEcommerceStatus(category, e)}
                            disabled={loading}
                            startIcon={category.isEcommerceEnabled ? <RemoveShoppingCartIcon /> : <ShoppingCartIcon />}
                          >
                            {category.isEcommerceEnabled ? t('stock.modals.category.actions.removeFromEcommerce') : t('stock.modals.category.actions.openEcommerce')}
                          </Button>
                          
                          <Box display="flex" gap={1}>
                            {category.level < 3 && (
                              <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectCategory(category);
                                }}
                                disabled={loading}
                                startIcon={<AddIcon />}
                              >
                                {t('stock.modals.category.actions.addSubShort')}
                              </Button>
                            )}
                            
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={(e) => handleDeleteCategory(category, e)}
                              disabled={loading}
                              startIcon={<DeleteIcon />}
                            >
                              {t('stock.common.delete')}
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>{t('stock.common.close')}</Button>
      </DialogActions>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* Uyarı Modalı */}
      <Dialog
        open={warningModalOpen}
        onClose={() => setWarningModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Alert severity="warning" icon={false} sx={{ py: 0, px: 1, bgcolor: 'transparent', border: 'none' }}>
              <Typography variant="h6" component="span" color="warning.main">
                {warningTitle}
              </Typography>
            </Alert>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {warningMessage}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('stock.modals.category.warnings.retryAfterFix')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setWarningModalOpen(false)} 
            variant="contained"
            color="primary"
          >
            {t('stock.modals.category.actions.gotIt')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Silme Onay Modalı */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">{t('stock.modals.category.delete.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('stock.modals.category.delete.irreversible')}
          </Alert>
          <Typography variant="body1" gutterBottom>
            <strong>"{categoryToDelete?.name}"</strong> {t('stock.modals.category.delete.confirmQuestion')}
          </Typography>
          {categoryToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('stock.modals.category.delete.details')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • {t('stock.modals.category.delete.level')}: {categoryToDelete.level}
              </Typography>
              <Typography variant="body2">
                • {t('stock.modals.category.table.branch')}: {getBranchName(categoryToDelete.branchesId)}
              </Typography>
              <Typography variant="body2">
                • {t('stock.modals.category.delete.ecommerce')}: {categoryToDelete.isEcommerceEnabled ? t('stock.modals.category.delete.active') : t('stock.modals.category.delete.passive')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseDeleteConfirm} 
            disabled={isDeleting}
            variant="outlined"
          >
            {t('stock.common.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteCategory} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? t('stock.modals.category.delete.deleting') : t('stock.modals.category.delete.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default InventoryCategoryModal; 